import { LuCheck, LuPencil } from "react-icons/lu";
import RadioButtonControlGroup from "../../../../../shared/RadioButtonControlGroup";
import "./PreEnrollmentTab.scss";

/**
 * Maps VITE_BACKEND_URL to the domain suffix shown in the URL helper text.
 * @param {string | undefined} backendUrl
 * @returns {"com" | "dev" | "live"}
 */
const resolvePreEnrollmentDomainSuffix = (backendUrl) => {
    if (typeof backendUrl !== "string") {
        return "com";
    }

    if (backendUrl.includes("centivo.dev")) {
        return "dev";
    }

    if (backendUrl.includes("centivo.live")) {
        return "live";
    }

    return "com";
};

const PRE_ENROLLMENT_DOMAIN_SUFFIX = resolvePreEnrollmentDomainSuffix(
    import.meta.env.VITE_BACKEND_URL,
);

/**
 * Custom open-enrollment setup guide, matching the plan-manager
 * "view instructions" link.
 * @constant {string}
 */
export const PRE_ENROLLMENT_SETUP_INSTRUCTIONS_URL =
    "https://centivo.atlassian.net/wiki/spaces/CEN/pages/6164152321/Custom+Open+Enrollment+Provider+Directory+Site+Instructions";

const CUSTOM_CONTENT_OPTIONS = [
    { value: "no", label: "No" },
    { value: "yes", label: "Yes" },
];

const YES_NO_OPTIONS = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

/**
 * Pre-enrollment tab: custom-content choice, the site URL field with a check
 * indicator and edit control, and the publish/unpublish action (or the custom
 * content notice). Presentational — all intent flows up via callbacks; owns no
 * state.
 *
 * @param {object} props
 * @param {Object} props.preEnrollment - `{ customContent, siteURL, publish, isOeUrlDisabled, isCustomContentDisabled }`.
 * @param {(customContent: boolean) => void} props.onCustomContentChange
 * @param {(displayPcpActivationBadge: boolean) => void} props.onDisplayPcpActivationBadgeChange
 * @param {(displayReferralRequiredBadge: boolean) => void} props.onDisplayReferralRequiredBadgeChange
 * @param {(value: string) => void} props.onSiteUrlChange
 * @param {(event: import('react').FocusEvent<HTMLInputElement>) => void} props.onSiteUrlBlur
 * @param {{ isUrlValid?: boolean, isUrlUnique?: boolean | null, shouldShowUrlCheck?: boolean }} props.urlValidation
 * @param {() => void} props.onEditUrlClick
 * @param {() => void} props.onPublishClick
 * @returns {import('react').JSX.Element}
 */
const PreEnrollmentTab = ({
    preEnrollment,
    onCustomContentChange,
    onDisplayPcpActivationBadgeChange,
    onDisplayReferralRequiredBadgeChange,
    onSiteUrlChange,
    onSiteUrlBlur,
    urlValidation,
    onEditUrlClick,
    onPublishClick,
}) => {
    const {
        customContent,
        siteURL,
        publish,
        isOeUrlDisabled,
        isCustomContentDisabled,
        displayPcpActivationBadge,
        displayReferralRequiredBadge,
    } = preEnrollment;
    const { isUrlValid, isUrlUnique, shouldShowUrlCheck } = urlValidation ?? {};
    const hasSiteUrl = Boolean((siteURL ?? "").trim());
    const showUrlCheck =
        shouldShowUrlCheck && isUrlValid === true && isUrlUnique === true;
    const isPublishDisabled = !hasSiteUrl;
    const isPublished = Boolean(publish);
    const areEditsLocked = isPublished || Boolean(isCustomContentDisabled);

    return (
        <div
            id="config-panel-preEnrollment"
            role="tabpanel"
            aria-labelledby="config-tab-preEnrollment"
            className="config-pre-enrollment-tab"
        >
            <p className="config-pre-enrollment-tab__intro">
                Use this section to configure the pre-enrollment site for this
                client. Submit an ITS ticket to request any visual or content
                customizations.
            </p>

            <div className="config-field">
                <span className="config-field__label">
                    Does this site require custom content?
                </span>
                <p className="config-field__helper">
                    Custom means: hero content, hero image, logo.
                </p>
                <RadioButtonControlGroup
                    ariaLabel="Requires custom content"
                    value={customContent ? "yes" : "no"}
                    disabled={areEditsLocked}
                    onChange={(value) => onCustomContentChange(value === "yes")}
                    options={CUSTOM_CONTENT_OPTIONS.map((option) => ({
                        ...option,
                        tone: publish ? "pending" : "default",
                    }))}
                />
            </div>

            <div className="config-field">
                <span className="config-field__label">
                    Display PCP Activation badge?
                </span>
                <RadioButtonControlGroup
                    ariaLabel="Display PCP Activation badge?"
                    value={displayPcpActivationBadge ? "yes" : "no"}
                    disabled={isPublished}
                    onChange={(value) =>
                        onDisplayPcpActivationBadgeChange(value === "yes")
                    }
                    options={YES_NO_OPTIONS}
                />
            </div>

            <div className="config-field">
                <span className="config-field__label">
                    Display Referrals Required badge?
                </span>
                <RadioButtonControlGroup
                    ariaLabel="Display Referrals Required badge?"
                    value={displayReferralRequiredBadge ? "yes" : "no"}
                    disabled={isPublished}
                    onChange={(value) =>
                        onDisplayReferralRequiredBadgeChange(value === "yes")
                    }
                    options={YES_NO_OPTIONS}
                />
            </div>

            <div className="form-input config-pre-enrollment-tab__url">
                <label htmlFor="site-url">Pre-enrollment site URL</label>
                <div className="config-pre-enrollment-tab__url-row">
                    <input
                        id="site-url"
                        type="text"
                        placeholder="Enter url"
                        maxLength={255}
                        disabled={
                            isOeUrlDisabled || areEditsLocked
                        }
                        value={siteURL}
                        onChange={(event) => onSiteUrlChange(event.target.value)}
                        onBlur={onSiteUrlBlur}
                    />
                    {showUrlCheck ? (
                        <span
                            className="config-pre-enrollment-tab__url-check"
                            aria-label="URL looks valid"
                        >
                            <LuCheck aria-hidden size={16} />
                        </span>
                    ) : null}
                    {isOeUrlDisabled && !areEditsLocked ? (
                        <button
                            type="button"
                            className="config-pre-enrollment-tab__edit-url"
                            aria-label="Edit URL"
                            onClick={onEditUrlClick}
                        >
                            <LuPencil aria-hidden size={16} />
                        </button>
                    ) : null}
                </div>
                <p className="config-field__helper">
                    Domain name must end with
                    {` "{Prefix}.centivo.${PRE_ENROLLMENT_DOMAIN_SUFFIX}"`}.
                </p>
                {isUrlUnique === false ? (
                    <p className="config-pre-enrollment-tab__url-error" role="alert">
                        URL already in use
                    </p>
                ) : null}
                {shouldShowUrlCheck && isUrlValid === false ? (
                    <p className="config-pre-enrollment-tab__url-error" role="alert">
                        URL is invalid
                    </p>
                ) : null}
            </div>

            {customContent ? (
                <div
                    className="config-alert config-alert--error"
                    role="note"
                >
                    <p className="config-alert__title">Custom content</p>
                    <ul>
                        <li>
                            Please submit an ITS ticket to request a custom PE
                            site,{" "}
                            <a
                                href={PRE_ENROLLMENT_SETUP_INSTRUCTIONS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                view instructions
                            </a>
                            .
                        </li>
                        <li>
                            Reach out to Digital Product if you need to unpublish
                            the site. This should only be necessary in case
                            clients request any retroactive visual customizations
                            or changes to the OE site URL
                        </li>
                        <li>
                            Any updates to the network configurations can be
                            performed on the &quot;Networks&quot; tab without
                            having to unpublish the site. Any changes to networks
                            will be reflected on all digital platforms.
                        </li>
                    </ul>
                </div>
            ) : (
                <>
                    {publish ? (
                        <div
                            className="config-alert config-alert--warning"
                            role="note"
                        >
                            <p className="config-alert__title">Warning</p>
                            <p>
                                Network configuration updates can be performed on
                                the Networks tab without unpublishing the site.
                                Only use Unpublish to temporarily disable the OE
                                site.
                            </p>
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className="config-pre-enrollment-tab__publish"
                        disabled={isPublishDisabled}
                        onClick={onPublishClick}
                    >
                        {publish ? "Unpublish" : "Publish"}
                    </button>
                </>
            )}
        </div>
    );
};

export default PreEnrollmentTab;
